import { useCallback } from 'react';
import {
  Box,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {
  CopyContentDto,
  CopyEntryDetailsDto,
  CopyType,
  DEFAULT_COPY_ENTRY,
} from '@act/shared/data-transfer-objects';
import DraggableEntry from './DraggableEntry';
import { memo } from 'react';

interface CopySectionFormProps {
  section: CopyContentDto;
  sectionIndex: number;
  onChange: (section: CopyContentDto) => void;
  onDeleteEntry: (sectionIndex: number, entryIndex: number) => void;
}

const CopySectionForm = memo(
  ({
    section,
    sectionIndex,
    onChange,
    onDeleteEntry,
  }: CopySectionFormProps) => {
    const handleAddEntry = useCallback(() => {
      const newEntry = {
        ...DEFAULT_COPY_ENTRY,
        copyBlocks: [],
        copyType: 'paragraph' as CopyType,
        sortOrder: section.entries.length + 1,
        id: `temp-${Date.now()}`,
      };

      onChange({
        ...section,
        entries: [...section.entries, newEntry],
      });
    }, [onChange, section]);

    const handleMoveEntry = useCallback(
      (dragIndex: number, hoverIndex: number) => {
        const dragEntry = section.entries[dragIndex];
        const newEntries = [...section.entries];
        newEntries.splice(dragIndex, 1);
        newEntries.splice(hoverIndex, 0, dragEntry);

        onChange({
          ...section,
          entries: newEntries.map((entry, index) => ({
            ...entry,
            sortOrder: index + 1,
          })),
        });
      },
      [onChange, section]
    );

    const handleEntryChange = useCallback(
      (index: number, updatedEntry: unknown) => {
        const newEntries = [...section.entries];
        newEntries[index] = updatedEntry as CopyEntryDetailsDto;

        onChange({
          ...section,
          entries: newEntries,
        });
      },
      [onChange, section]
    );

    const handleTitleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;

        onChange({
          ...section,
          title: newValue,
        });
      },
      [onChange, section]
    );

    const handleHighlightedChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.checked;

        onChange({
          ...section,
          isHighlighted: newValue,
        });
      },
      [onChange, section]
    );

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', gap: '16px' }}>
          <TextField
            label="Section Title"
            value={section.title || ''}
            onChange={handleTitleChange}
            fullWidth
          />

          <FormControlLabel
            label="Highlighted"
            control={
              <Checkbox
                checked={section.isHighlighted || false}
                onChange={handleHighlightedChange}
              />
            }
          />
        </Box>

        {section.entries.map((entry, index) => (
          <DraggableEntry
            key={`${section.type}-${section.title}-${index}`}
            entry={entry}
            index={index}
            sectionIndex={sectionIndex}
            onMove={handleMoveEntry}
            onChange={handleEntryChange}
            onDelete={() => onDeleteEntry(sectionIndex, index)}
            type="copy"
          />
        ))}

        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAddEntry}
        >
          Add Entry
        </Button>
      </Box>
    );
  }
);

export default CopySectionForm;
